import React from 'react';
import { useFormContext } from 'react-hook-form';
import { Stack } from '@mui/material';
import {
  SignupFormData,
  SignUpUserType,
  SignupStep,
  EligibilityCheckMethod,
} from 'features/signUp/types';
import { useSignupData } from 'features/signUp/hooks/useSignupData';
import { useTranslationContext } from 'contexts/TranslationContext/TranslationContext';
import { AccessCodeSubmissionStep } from './Steps/AccessCodeSubmissionStep';
import {
  AccessCodeMatchedStep,
  ShortCutSSO,
  EligibilityConfirmedStep,
  SSOSignUpSuccessStep,
  WelcomeStep,
} from './Steps/TransitionSteps';
import { SignUpUserTypeStep } from './Steps/SignUpUserTypeStep';
import { MemberIdEligibilityCheckStep } from './Steps/MemberIdEligibilityCheckStep';
import { LastNameEligibilityCheckStep } from './Steps/LastNameEligibilityCheckStep';
import { DobEligibilityCheckStep } from './Steps/DobEligibilityCheckStep';
import { ProfileCreationStep } from './Steps/ProfileCreationStep/ProfileCreationStep';
import { AccountCredentialsStep } from './Steps/AccountCredentialsStep/AccountCredentialsStep';
import { PrivacyPolicyAcceptanceStep } from './Steps/PrivacyPolicyAcceptanceStep';
import { SignUpSubmitButton } from './SignUpSubmitButton';
import { LoadingStep } from './Steps/LoadingStep/LoadingStep';
import { SignUpHelpButton } from './SignUpHelpButton';

interface SignUpFormProps {
  currentStep: SignupStep;
  userType: SignUpUserType;
  eligibilityCheckMethod?: EligibilityCheckMethod;
  isApiRequestInProgress: boolean;
  onSubmit: (formData: SignupFormData) => Promise<void>;
  openHelpDialog: () => void;
}
export const SignUpForm = ({
  currentStep,
  userType,
  eligibilityCheckMethod,
  isApiRequestInProgress,
  onSubmit,
  openHelpDialog,
}: SignUpFormProps) => {
  const { handleSubmit } = useFormContext<SignupFormData>();
  const { caregiverRelationships, hospitalDisplayName, privacyPolicy, eulaPolicy } =
    useSignupData();
  const { translation } = useTranslationContext();

  return (
    <form onSubmit={handleSubmit(onSubmit)} noValidate>
      <Stack spacing={4}>
        {currentStep === 'AccessCodeSubmission' && <AccessCodeSubmissionStep />}
        {currentStep === 'AccessCodeMatched' && <AccessCodeMatchedStep signUpUserType={userType} />}
        {currentStep === 'UserTypeSelection' && <SignUpUserTypeStep onSubmit={onSubmit} />}
        {currentStep === 'ShortCutSSO' && (
          <ShortCutSSO hospitalDisplayName={hospitalDisplayName || ''} />
        )}
        {currentStep === 'ConditionalEligibilityCheck' &&
          (eligibilityCheckMethod === 'memberId' ? (
            <MemberIdEligibilityCheckStep signUpUserType={userType} />
          ) : (
            <LastNameEligibilityCheckStep signUpUserType={userType} />
          ))}
        {currentStep === 'DobEligibilityCheck' && (
          <DobEligibilityCheckStep signUpUserType={userType} />
        )}
        {currentStep === 'EligibilityVerified' && (
          <EligibilityConfirmedStep signUpUserType={userType} />
        )}
        {currentStep === 'CaregiverProfileCreation' && (
          <ProfileCreationStep
            profileType="caregiver"
            caregiverMemberRelationships={caregiverRelationships}
          />
        )}
        {currentStep === 'MemberProfileCreation' && (
          <ProfileCreationStep
            profileType={userType === 'caregiver' ? 'memberByCaregiverProxy' : 'member'}
            caregiverMemberRelationships={caregiverRelationships}
          />
        )}
        {currentStep === 'AccountCredentials' && <AccountCredentialsStep />}
        {currentStep === 'SSOSignupLoading' && (
          <LoadingStep messages={[translation('working_on_it')]} />
        )}
        {currentStep === 'PolicyAcceptance' && (
          <PrivacyPolicyAcceptanceStep
            privacyPolicyUrl={privacyPolicy?.url ?? ''}
            eulaPolicyUrl={eulaPolicy?.url ?? ''}
          />
        )}
        {currentStep === 'SSOSignUpSuccess' && <SSOSignUpSuccessStep />}
        {currentStep === 'Welcome' && <WelcomeStep />}

        {/* Centralized Submit Button */}
        <SignUpSubmitButton
          currentStep={currentStep}
          userType={userType}
          isSubmitting={isApiRequestInProgress}
        />
        <SignUpHelpButton currentStep={currentStep} openHelpDialog={openHelpDialog} />
      </Stack>
    </form>
  );
};
